import type { UseAgentPanelsReturn } from '../../hooks/use-agent-panels';

import { renderPanelComponent } from './panels/registry';

interface Props {
  hook: UseAgentPanelsReturn;
}

const PANEL_WIDTH = 320;

/**
 * Right-side dock inside the ConversationThread. Opens automatically when
 * the agent updates a panel, closes via the close button. Tabs along the
 * top when multiple panels exist; each tab's `components` render stacked
 * in the content area via the A2UI component registry.
 */
export function AgentPanelDock({ hook }: Props) {
  const { tabs, activeTabId, isPanelOpen, setActiveTab, closePanel } = hook;
  if (!isPanelOpen || tabs.length === 0) return null;

  const active = tabs.find((t) => t.tab_id === activeTabId) ?? tabs[0];

  return (
    <aside
      style={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--border)',
        background: 'var(--bg-alt)',
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-alt2)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-soft)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontWeight: 600,
          }}
        >
          Agent panel
        </div>
        <button
          type="button"
          onClick={closePanel}
          title="Close panel"
          aria-label="Close agent panel"
          style={{
            marginLeft: 'auto',
            width: 24,
            height: 24,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            background: 'transparent',
            color: 'var(--text-soft)',
            cursor: 'pointer',
            borderRadius: 4,
            fontSize: 14,
          }}
        >
          ×
        </button>
      </div>

      {tabs.length > 1 && (
        <div
          role="tablist"
          style={{
            display: 'flex',
            overflowX: 'auto',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-alt)',
          }}
        >
          {tabs.map((t) => {
            const isActive = active && t.tab_id === active.tab_id;
            return (
              <button
                key={t.tab_id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(t.tab_id)}
                style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'var(--text)' : 'var(--text-soft)',
                  background: 'transparent',
                  border: 0,
                  borderBottom: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
                  cursor: 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  maxWidth: 160,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontFamily: 'inherit',
                }}
              >
                {t.title}
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {active ? (
          active.components.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-mute)', textAlign: 'center', padding: 16 }}>
              Empty panel.
            </div>
          ) : (
            active.components.map((c) => renderPanelComponent(c))
          )
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-mute)', textAlign: 'center', padding: 16 }}>
            No panel selected.
          </div>
        )}
      </div>
    </aside>
  );
}

interface ToggleProps {
  hook: UseAgentPanelsReturn;
}

export function AgentPanelToggle({ hook }: ToggleProps) {
  if (hook.tabs.length === 0) return null;
  return (
    <button
      type="button"
      onClick={hook.togglePanel}
      title={hook.isPanelOpen ? 'Hide agent panel' : 'Show agent panel'}
      aria-label={hook.isPanelOpen ? 'Hide agent panel' : 'Show agent panel'}
      style={{
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        border: `1px solid ${hook.isPanelOpen ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 6,
        background: hook.isPanelOpen ? 'var(--accent-soft)' : 'var(--surface)',
        color: hook.isPanelOpen ? 'var(--accent)' : 'var(--text-soft)',
        cursor: 'pointer',
      }}
    >
      🧩
    </button>
  );
}
